import { IonModal } from '@ionic/react'
import classNames from 'classnames'
import React, {
  Fragment,
  useEffect,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from 'react'

import { type SearchedExam } from '../api/interfaces/sconnStudyData'
import { useSearchExams } from '../module/sconnStudyData/hook'
import { useDebouncedValue } from '../util/hooks/useDebouncedValue'

import Spinner from './common/Spinner'
import { CloseIcon, SearchIcon } from './icons/icon'

const MAX_SEARCH_TEXT_LENGTH = 30

interface ExamSearchModalProps {
  open: boolean
  isSelectionLimitReached: boolean
  isExamSelected: (exam: SearchedExam) => boolean
  onLimitReached: () => void
  onSelectExam: (exam: SearchedExam) => void
  onAddCustomExam: (label: string) => void
  onClose: () => void
}

function HighlightedText({ text, query }: { text: string; query: string }) {
  const keyword = query.trim()

  if (!keyword) {
    return <>{text}</>
  }

  const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const parts = text.split(new RegExp(`(${escapedKeyword})`, 'gi'))

  return (
    <>
      {parts.map((part, index) =>
        part.toLowerCase() === keyword.toLowerCase() ? (
          <span key={`${part}-${index}`} className='text-text-blue-primary'>
            {part}
          </span>
        ) : (
          <Fragment key={`${part}-${index}`}>{part}</Fragment>
        ),
      )}
    </>
  )
}

// V2 시험 검색 모달: 서버 검색(POST /search/cert) 결과 선택 또는 직접 입력(custom) 추가
export default function ExamSearchModal(props: ExamSearchModalProps) {
  const [searchText, setSearchText] = useState<string>('')
  const debouncedText = useDebouncedValue(searchText.trim())
  const hasSearchText = searchText.trim().length > 0
  const isTextLimitReached = searchText.length >= MAX_SEARCH_TEXT_LENGTH
  const shouldSearch = props.open && hasSearchText && !isTextLimitReached

  const { data, isFetching } = useSearchExams(shouldSearch ? debouncedText : '')

  useEffect(() => {
    if (!props.open) {
      setSearchText('')
    }
  }, [props.open])

  const searchResults = data ?? []
  const showLoading = shouldSearch && isFetching && searchResults.length === 0
  const showEmpty = shouldSearch && !isFetching && data !== undefined && searchResults.length === 0

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value.slice(0, MAX_SEARCH_TEXT_LENGTH))
  }

  // Enter 입력 시 바깥 설문 폼(form#survey-form)이 제출되는 것을 방지
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
    }
  }

  const handleAddCustomExam = () => {
    if (!searchText.trim()) {
      return
    }

    if (props.isSelectionLimitReached) {
      props.onLimitReached()
      return
    }

    props.onAddCustomExam(searchText)
  }

  return (
    <IonModal id='sheet-modal' keepContentsMounted isOpen={props.open} backdropDismiss={false}>
      <div className='flex flex-col h-full p-[24px]'>
        <div className='flex items-center justify-between'>
          <h2 className='text-[18px] font-semibold text-text-black-primary'>준비 중인 시험 찾기</h2>
          <button
            type='button'
            aria-label='시험 검색 닫기'
            className='flex h-[36px] w-[36px] items-center justify-center'
            onClick={props.onClose}
          >
            <CloseIcon />
          </button>
        </div>

        {/* 설문 폼(form#survey-form) 안에서 렌더링되므로 중첩 form 대신 div 사용 */}
        <div className='pt-[24px] pb-[8px]'>
          <div className='flex h-[40px] w-full items-center rounded-[20px] border-[0.5px] border-sub-border bg-white px-[18px]'>
            <input
              autoFocus
              value={searchText}
              onChange={handleSearchChange}
              onKeyDown={handleKeyDown}
              maxLength={MAX_SEARCH_TEXT_LENGTH}
              placeholder='검색어를 입력하세요.'
              className='min-w-0 flex-1 bg-transparent text-[14px] text-text-black-primary placeholder-sub-font focus:outline-none'
            />
            <div className='flex h-[32px] w-[32px] shrink-0 items-center justify-center'>
              {isFetching && hasSearchText ? (
                <Spinner width={24} height={24} />
              ) : (
                <SearchIcon stroke='black' />
              )}
            </div>
          </div>
          <div className='flex min-h-[24px] items-center justify-between gap-[16px] px-[8px] pt-[8px]'>
            <span
              className={classNames(
                'text-[12px]',
                isTextLimitReached ? 'text-invalid-msg' : 'text-transparent',
              )}
            >
              30자 이내로 입력해 주세요
            </span>
            <span
              className={classNames(
                'shrink-0 text-[12px]',
                isTextLimitReached ? 'text-invalid-msg' : 'text-sub-font',
              )}
            >
              ({searchText.length}/{MAX_SEARCH_TEXT_LENGTH})
            </span>
          </div>
        </div>

        <div className='flex-1 overflow-y-auto'>
          {!hasSearchText ? (
            <div className='flex h-full min-h-[220px] items-center justify-center text-center'>
              <p className='text-[14px] text-text-black-secondary'>
                찾고 있는 시험명을 입력하면
                <br />
                관련 시험을 바로 추가할 수 있어요.
              </p>
            </div>
          ) : isTextLimitReached ? null : showLoading ? (
            <div className='flex h-full min-h-[220px] items-center justify-center'>
              <div className='flex items-center gap-[8px]'>
                <Spinner width={24} height={24} />
                <p className='text-[14px] text-text-black-secondary'>시험을 찾는 중이에요.</p>
              </div>
            </div>
          ) : showEmpty ? (
            <div className='flex h-full min-h-[220px] flex-col items-center gap-[8px]'>
              <div className='flex flex-1 flex-col items-center justify-center'>
                <p className='py-[8px] text-[14px] text-text-black-tertiary'>
                  일치하는 시험결과가 없어요
                </p>
              </div>
              <button
                type='button'
                onClick={handleAddCustomExam}
                className='flex h-[98px] w-full flex-row gap-[8px] rounded-[12px] border border-common-line bg-surface-bg p-[16px] text-left'
              >
                <img
                  src={`${process.env.PUBLIC_URL}/img/learning-keyword/ic_sconn_01.svg`}
                  alt=''
                  aria-hidden='true'
                />
                <div className='flex flex-1 flex-col justify-center gap-[4px]'>
                  <span className='text-[14px] font-medium text-text-blue-primary'>
                    {searchText}
                  </span>
                  <span className='text-[14px] text-text-black-secondary'>
                    나의 학습 목표로 설정하기
                  </span>
                </div>
              </button>
            </div>
          ) : (
            <div className='flex flex-col'>
              {searchResults.map((exam) => {
                const isSelected = props.isExamSelected(exam)
                const isDisabled = props.isSelectionLimitReached && !isSelected

                return (
                  <button
                    key={exam.examId}
                    type='button'
                    aria-disabled={isDisabled}
                    onClick={() => {
                      if (isDisabled) {
                        props.onLimitReached()
                        return
                      }

                      if (!isSelected) {
                        props.onSelectExam(exam)
                      }

                      props.onClose()
                    }}
                    className={classNames(
                      'flex h-[40px] w-full items-center justify-between gap-[24px] px-[8px] text-left',
                      isSelected ? 'rounded-[12px] bg-surface-bg' : 'border-b border-common-line',
                      isDisabled && 'cursor-not-allowed opacity-60',
                    )}
                  >
                    <span className='min-w-0 flex-1 truncate text-[14px] text-text-black-primary'>
                      <HighlightedText text={exam.examName} query={searchText} />
                    </span>
                    {exam.departmentName && (
                      <span className='shrink-0 text-right text-[12px] text-text-black-tertiary'>
                        <HighlightedText text={exam.departmentName} query={searchText} />
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </IonModal>
  )
}
